import jsPDF from "jspdf";
import autoTable, { type CellHookData } from "jspdf-autotable";
import { payslipTaxTypeLabel, type Payslip, type PayslipTaxItem } from "./hr-store";
import { monthLabel } from "./format";

const TABLE_RADIUS = 6;

const COMPANY = {
  name: "PT Rekayasa Teknologi Cerdas",
  address: "Jakarta, Indonesia",
};

/** Palet slip gaji — selaras tema sage / hijau hutan (bersih, tanpa warna aksen berlebihan) */
const C = {
  primary: [26, 54, 47] as [number, number, number],
  primaryMuted: [45, 78, 70] as [number, number, number],
  text: [26, 46, 42] as [number, number, number],
  textMuted: [90, 107, 103] as [number, number, number],
  border: [209, 219, 216] as [number, number, number],
  surface: [248, 250, 249] as [number, number, number],
  rowHighlight: [237, 244, 241] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  deduction: [185, 28, 28] as [number, number, number],
  deductionMuted: [127, 29, 29] as [number, number, number],
};

/** Angka IDR aman untuk jsPDF (Helvetica): tanpa simbol Unicode yang rusak di PDF. */
function formatIdrPdf(n: number): string {
  const v = Math.round(n || 0);
  return `Rp ${v.toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;
}

/** Potongan: tanda kurung agar jelas tanpa karakter minus Unicode. */
function formatDeductionPdf(n: number): string {
  return `(${formatIdrPdf(n)})`;
}

function taxRowDescription(item: PayslipTaxItem): string {
  const base = payslipTaxTypeLabel(item.jenis);
  return item.catatan?.trim() ? `${base} - ${item.catatan.trim()}` : base;
}

const TABLE_AMOUNT_COL_W = 118;

/** Sudut membulat + header/footer baris total; border luar digambar setelah tabel selesai. */
function createRoundedTableHooks(headFill: [number, number, number]) {
  return {
    tableLineWidth: 0,
    didParseCell: (data: CellHookData) => {
      const { section, row, table } = data;
      const isHead = section === "head";
      const isLastBodyRow = section === "body" && row.index === table.body.length - 1;

      const lw = { top: 0, left: 0, right: 0, bottom: 0 };
      if (isHead && table.body.length > 0) lw.bottom = 0.25;
      else if (section === "body" && !isLastBodyRow) lw.bottom = 0.25;
      data.cell.styles.lineWidth = lw;

      if (isHead) {
        data.cell.styles.fillColor = false;
        data.cell.styles.textColor = 255;
      }
      if (isLastBodyRow) {
        data.cell.styles.fillColor = false;
      }
    },
    willDrawCell: (data: CellHookData) => {
      const pdf = data.doc as jsPDF;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const x = data.settings.margin.left;
      const tableW = data.table.getWidth(pageWidth);
      const y = data.cell.y;
      const h = data.row.height;

      if (data.section === "head" && data.column.index === 0) {
        pdf.setFillColor(...headFill);
        pdf.roundedRect(x, y, tableW, h, TABLE_RADIUS, TABLE_RADIUS, "F");
        pdf.rect(x, y + h - TABLE_RADIUS, tableW, TABLE_RADIUS, "F");
      }

      if (data.section === "body" && data.column.index === 0 && data.row.index === data.table.body.length - 1) {
        pdf.setFillColor(...C.rowHighlight);
        pdf.roundedRect(x, y, tableW, h, TABLE_RADIUS, TABLE_RADIUS, "F");
        pdf.rect(x, y, tableW, TABLE_RADIUS, "F");
      }
    },
  };
}

function drawRoundedTableFrame(doc: jsPDF) {
  const table = (doc as unknown as {
    lastAutoTable?: {
      settings: { startY: number; margin: { left: number } };
      finalY?: number;
      getWidth: (pageWidth: number) => number;
    };
  }).lastAutoTable;
  if (!table?.finalY) return;
  const pageWidth = doc.internal.pageSize.getWidth();
  const x = table.settings.margin.left;
  const y = table.settings.startY;
  const w = table.getWidth(pageWidth);
  const h = table.finalY - y;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.75);
  doc.roundedRect(x, y, w, h, TABLE_RADIUS, TABLE_RADIUS, "S");
}

function moneyTableOptions(doc: jsPDF, startY: number, margin: number, headFill: [number, number, number]) {
  const W = doc.internal.pageSize.getWidth();
  const tableWidth = W - 2 * margin;
  return {
    startY,
    theme: "plain" as const,
    tableWidth,
    headStyles: { fillColor: headFill, textColor: 255, fontSize: 10, lineWidth: 0, font: "helvetica" },
    styles: {
      font: "helvetica",
      fontSize: 10,
      cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
      textColor: C.text,
      lineColor: C.border,
      lineWidth: 0.25,
      overflow: "linebreak" as const,
    },
    columnStyles: {
      0: { cellWidth: tableWidth - TABLE_AMOUNT_COL_W },
      1: { cellWidth: TABLE_AMOUNT_COL_W, halign: "right" as const },
    },
    margin: { left: margin, right: margin },
    ...createRoundedTableHooks(headFill),
  };
}

/** Sementara dinonaktifkan: tabel «Ringkasan jam kerja» di slip gaji. Ubah ke `true` untuk menampilkan lagi. */
const SHOW_PAYSLIP_HOURS_SUMMARY = false;

/** Konversi YYYY-MM-DD ke "dd Mmm yyyy" (id-ID); kembalikan input apa adanya jika tidak valid. */
function formatYmdId(s: string | null | undefined): string {
  if (!s) return "-";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s));
  if (!m) return String(s);
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(dt.getTime())) return String(s);
  return dt.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}

type LastAutoTable = { lastAutoTable: { finalY: number } };

function tableEnd(doc: jsPDF): number {
  return (doc as unknown as LastAutoTable).lastAutoTable.finalY;
}

/** Pastikan ada ruang vertikal cukup; kalau tidak, tambah halaman baru dan kembalikan Y baru. */
function ensureSpace(doc: jsPDF, y: number, needed: number, marginBottom = 60): number {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - marginBottom) {
    doc.addPage();
    return 60;
  }
  return y;
}

export function downloadPayslipPDF(p: Payslip) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 40;

  /* ----- HEADER ----- */
  doc.setFillColor(...C.primary);
  doc.rect(0, 0, W, 88, "F");
  doc.setTextColor(...C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("SLIP GAJI KARYAWAN", M, 36);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const periodLabel = monthLabel(p.period);
  const rangeLabel =
    p.periodStartDate && p.periodEndDate
      ? `${formatYmdId(p.periodStartDate)} s/d ${formatYmdId(p.periodEndDate)}`
      : null;
  doc.text(`Periode: ${periodLabel}${rangeLabel ? `  (${rangeLabel})` : ""}`, M, 56);
  doc.text(
    `Diterbitkan: ${new Date().toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}`,
    M,
    72,
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(COMPANY.name, W - M, 36, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(COMPANY.address, W - M, 52, { align: "right" });
  doc.setFontSize(8);
  doc.setTextColor(230, 236, 233);
  doc.text("Dokumen rahasia - hanya untuk karyawan ybs.", W - M, 68, { align: "right" });

  /* ----- INFO KARYAWAN ----- */
  let y = 108;
  doc.setTextColor(...C.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Informasi Karyawan", M, y);
  doc.setDrawColor(...C.border);
  doc.line(M, y + 3, W - M, y + 3);
  y += 16;

  const infoRows: [string, string][] = [
    ["Nama", p.employee.name],
    ["Jabatan", p.employee.position || "-"],
    ["Departemen", p.employee.department || "-"],
  ];
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const labelColW = 88;
  const colonX = M + labelColW;
  const valueX = colonX + 10;
  const valueMaxW = W - M - valueX;
  for (const [k, v] of infoRows) {
    doc.setTextColor(...C.textMuted);
    doc.text(k, M, y);
    doc.setTextColor(...C.text);
    doc.text(":", colonX, y);
    doc.text(String(v), valueX, y, { maxWidth: valueMaxW });
    y += 16;
  }
  y += 4;

  /* ----- RINGKASAN JAM KERJA (opsional, lihat SHOW_PAYSLIP_HOURS_SUMMARY) ----- */
  if (SHOW_PAYSLIP_HOURS_SUMMARY) {
    autoTable(doc, {
      startY: y + 4,
      head: [["Ringkasan jam kerja", "Nilai"]],
      body: [
        ["Jam tercatat (Hivedesk)", `${p.baseHoursWorked.toFixed(2)} jam`],
        ["Penyesuaian jam", `${p.adjustmentHours >= 0 ? "+" : "-"}${Math.abs(p.adjustmentHours).toFixed(2)} jam`],
        ["Cuti (efektif)", `${p.leaveHours.toFixed(2)} jam`],
        [
          { content: "Total jam dibayar", styles: { fontStyle: "bold" } },
          { content: `${p.hoursWorked.toFixed(2)} jam`, styles: { fontStyle: "bold" } },
        ],
        ["Tarif per jam", formatIdrPdf(p.hourlyRate)],
        ["Aktivitas rata-rata", `${(p.activityPercentage || 0).toFixed(0)}%`],
      ],
      theme: "plain",
      headStyles: { fillColor: C.primary, textColor: 255, fontSize: 10, lineWidth: 0 },
      styles: {
        fontSize: 9,
        cellPadding: 6,
        textColor: C.text,
        lineColor: C.border,
        lineWidth: 0.25,
      },
      alternateRowStyles: { fillColor: C.surface },
      columnStyles: { 1: { halign: "right" } },
      margin: { left: M, right: M },
      ...createRoundedTableHooks(C.primary),
    });
    drawRoundedTableFrame(doc);
    y = tableEnd(doc) + 12;
  } else {
    y += 8;
  }

  /* ----- PENDAPATAN: semua komponen selalu ditampilkan (0 jika tidak ada) ----- */
  const incomeBody: Array<
    [string, string] | [{ content: string; styles?: Record<string, unknown> }, { content: string; styles?: Record<string, unknown> }]
  > = [];
  incomeBody.push(["Pendapatan Pokok", formatIdrPdf(p.basicSalary)]);
  incomeBody.push(["Insentif WFO", formatIdrPdf(p.insentifWFO)]);
  incomeBody.push(["Insentif Jabatan", formatIdrPdf(p.insentifJabatan)]);
  incomeBody.push(["Reimburse", formatIdrPdf(p.totalReimburse)]);
  incomeBody.push(["Cuti", formatIdrPdf(p.paidLeave?.amount ?? 0)]);
  incomeBody.push([
    { content: "Total pendapatan (bruto)", styles: { fontStyle: "bold", fillColor: C.rowHighlight } },
    { content: formatIdrPdf(p.bruto), styles: { fontStyle: "bold", halign: "right", fillColor: C.rowHighlight } },
  ]);
  autoTable(doc, {
    ...moneyTableOptions(doc, y, M, C.primaryMuted),
    head: [["Komponen pendapatan", "Jumlah (IDR)"]],
    body: incomeBody as never,
    alternateRowStyles: { fillColor: C.surface },
  });
  drawRoundedTableFrame(doc);
  y = tableEnd(doc) + 12;

  /* ----- POTONGAN PENDAPATAN (selalu ditampilkan) ----- */
  const bpjsKesehatan = 0;
  const bpjsKetenagakerjaan = 0;
  const totalPotongan = p.totalTax + bpjsKesehatan + bpjsKetenagakerjaan;
  y = ensureSpace(doc, y, 56 + (p.taxItems.length + 2) * 24);
  const deductionRows: Array<
    string[] | [{ content: string; styles?: Record<string, unknown> }, { content: string; styles?: Record<string, unknown> }]
  > = [
    ["BPJS Kesehatan", formatDeductionPdf(bpjsKesehatan)],
    ["BPJS Ketenagakerjaan", formatDeductionPdf(bpjsKetenagakerjaan)],
  ];
  for (const item of p.taxItems) {
    deductionRows.push([taxRowDescription(item), formatDeductionPdf(item.nominal)]);
  }
  deductionRows.push([
    { content: "Total potongan pendapatan", styles: { fontStyle: "bold", fillColor: C.rowHighlight } },
    {
      content: formatDeductionPdf(totalPotongan),
      styles: { fontStyle: "bold", halign: "right", fillColor: C.rowHighlight },
    },
  ]);

  autoTable(doc, {
    ...moneyTableOptions(doc, y, M, C.deductionMuted),
    head: [["Potongan pendapatan", "Jumlah (IDR)"]],
    body: deductionRows as never,
    alternateRowStyles: { fillColor: [252, 245, 245] },
    columnStyles: {
      0: { cellWidth: W - 2 * M - TABLE_AMOUNT_COL_W },
      1: { cellWidth: TABLE_AMOUNT_COL_W, halign: "right", textColor: C.deduction },
    },
  });
  drawRoundedTableFrame(doc);
  y = tableEnd(doc) + 12;

  /* ----- NETTO ----- */
  y = ensureSpace(doc, y, 72);
  const nettoBoxH = 64;
  const nettoBoxW = W - 2 * M;
  const nettoCenterX = M + nettoBoxW / 2;
  doc.setFillColor(...C.primary);
  doc.roundedRect(M, y, nettoBoxW, nettoBoxH, 6, 6, "F");
  doc.setTextColor(...C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("GAJI BERSIH (TAKE HOME PAY)", nettoCenterX, y + 24, { align: "center" });
  doc.setFontSize(20);
  doc.text(formatIdrPdf(p.netto), nettoCenterX, y + 48, { align: "center" });
  y += nettoBoxH + 12;

  /* ----- FOOTER ----- */
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setTextColor(...C.textMuted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`Slip Gaji ${p.employee.name} - Periode ${periodLabel}`, M, doc.internal.pageSize.getHeight() - 20);
    doc.text(`Halaman ${i} dari ${pageCount}`, W - M, doc.internal.pageSize.getHeight() - 20, { align: "right" });
    doc.text(
      "Dokumen ini diterbitkan secara elektronik dan sah tanpa tanda tangan basah.",
      W / 2,
      doc.internal.pageSize.getHeight() - 32,
      { align: "center" },
    );
  }

  doc.save(`Slip-Gaji-${p.employee.name.replace(/\s+/g, "-")}-${p.period}.pdf`);
}
